import { useMemo } from 'react'
import { isImberaUser } from '../store/helpers/UserHelper'

/**
 * Filters a datagrid column configuration down to the columns the current account may see,
 * dropping any entry flagged `internalOnly` when the account is not Imbera-internal.
 *
 * Every view that renders the Services table shares the same `useConfigurator` storage key
 * (`dashboard-services-datagrid-config`), and `mergeConfiguration` reconciles the saved
 * configuration against whatever default array it is given, matching by `title`. If one view
 * filtered a column out while another did not, the saved configuration would be rewritten on
 * every switch between them — one view dropping the entry, the other re-adding it visible.
 * That is why the filtering lives in a shared hook instead of at a single call site: every
 * consumer of that storage key has to agree on the same column set.
 *
 * The result is memoized on the `isImberaUser` boolean rather than on `userProfile`, for two
 * reasons: `useConfigurator` lists its `defaultConfiguration` argument in the dependency array
 * of an effect that calls `setConfiguration` unconditionally, so an array rebuilt on every
 * render would loop; and `withImmutablePropsToJS` may hand down a new `userProfile` object
 * identity per render, which would defeat a memo keyed on the object.
 *
 * @param {Array} columnConfig The full column configuration (a module-level constant)
 * @param {Object} userProfile The authenticated user's profile
 * @returns {Array} The columns visible to this account
 */
export const useVisibleColumns = (columnConfig, userProfile) => {
	const isInternalUser = isImberaUser(userProfile)
	return useMemo(() => columnConfig.filter((column) => !column.internalOnly || isInternalUser), [columnConfig, isInternalUser])
}
